import { useState, useEffect } from 'react';
import { Modal, message } from 'antd';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import ManageNominatedRewardModal from '../../presentation/ManageNominatedRewardModal';

import useManageNominatedReward from '../../hooks/useManageNominatedReward';
import { withApollo } from 'react-apollo';
import { fetchFormsByConnectionId } from 'src/_shared/services/utils.js';

import { nominationSchema } from './validationSchema.js';

const ManageNominatedRewardContainer = ({
	showModal,
	create = false,
	setShowModal,
	getNominationConfiguration,
	nomineeCount,
	nominationConfig = {},
	client, // Client from withApollo HOC
}) => {
	const currentUser = useSelector((state) => state?.user?.currentUser);

	const [showLoader, setShowLoader] = useState(false);
	const [configLoading, setConfigLoading] = useState(false);
	const [initialData, setInitialData] = useState(nominationConfig);
	const isComplete = nominationConfig?.status === 'Complete';
	const [nominationForms, setNominationForms] = useState([]);
	const [nominationFormsLoading, setNominationFormsLoading] = useState(false);

	useEffect(() => {
		setInitialData(nominationConfig);
	}, [nominationConfig]);

	useEffect(() => {
		if (showModal && currentUser?.companyId) {
			const loadNominationForms = async () => {
				setNominationFormsLoading(true);
				try {
					const forms = await fetchFormsByConnectionId(
						currentUser,
						'nomination_form',
						client
					);
					setNominationForms(forms);
				} catch (error) {
					console.error('Error loading nomination forms:', error);
					setNominationForms([]);
				} finally {
					setNominationFormsLoading(false);
				}
			};
			loadNominationForms();
		}
	}, [showModal, currentUser?.companyId]);

	const {
		empLevel,
		badgeList,
		empLoginType,
		empPayType,
		loading,
		createUpdateNominationConfiguration,
	} = useManageNominatedReward({ client, currentUser }); // Pass client and currentUser to the hook

	const handleCancel = () => {
		setShowModal && setShowModal(false);
	};

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			id: initialData?.id,
			customFormId: initialData?.customFormId || null,
			badgeId: initialData?.badge?.id,
			startDate: initialData?.startDate,
			endDate: initialData?.endDate,
			nominationCriteria: {
				nominee: {
					employeeLevelId: initialData?.nomineeEmployeeLevelId
						? initialData?.nomineeEmployeeLevelId
						: [],
					employeelogInTypeId: initialData?.nomineeEmployeelogInTypeId
						? initialData?.nomineeEmployeelogInTypeId
						: undefined,
					employeePayTypeId: initialData?.nomineeEmployeePayTypeId
						? initialData?.nomineeEmployeePayTypeId
						: undefined,
				},
				nominator: {
					employeeLevelId: initialData?.nominatorEmployeeLevelId
						? initialData?.nominatorEmployeeLevelId
						: [],
					employeelogInTypeId: initialData?.nominatorEmployeelogInTypeId
						? initialData?.nominatorEmployeelogInTypeId
						: undefined,
					employeePayTypeId: initialData?.nominatorEmployeePayTypeId
						? initialData?.nominatorEmployeePayTypeId
						: undefined,
					awardedCount: initialData?.awardedCount
						? initialData?.awardedCount
						: undefined,
				},
			},
		},
		validationSchema: nominationSchema,
		onSubmit: async (values) => {
			setShowLoader(true);
			try {
				const result = await createUpdateNominationConfiguration(values);
				if (result) {
					handleCancel();
					const successMessage = `Nomination Period ${values.id ? 'Updated' : 'Created'} Successfully`;
					message.success(successMessage);
					getNominationConfiguration();
				} else {
					message.error(
						`Error ${values.id ? 'Updating' : 'Creating'} Nomination Period`
					);
				}
			} catch (error) {
				const errorMessage =
					error?.response?.data?.message ||
					`Error ${values.id ? 'Updating' : 'Creating'} Nomination Period`;
				message.error(errorMessage);
			} finally {
				setShowLoader(false);
			}
		},
	});

	return (
		<Modal
			open={showModal}
			title={`${create ? 'Create New Nomination Period' : 'Nomination Details'}`}
			closable={true}
			onCancel={handleCancel}
			footer={null}
			width={'1000px'}
			className="manage-nominated-reward-modal reward-modal"
		>
			<ManageNominatedRewardModal
				create={create}
				badgeList={badgeList}
				empLevel={empLevel}
				empLoginType={empLoginType}
				empPayType={empPayType}
				formik={formik}
				loading={loading || configLoading}
				showLoader={showLoader}
				nominatedCount={nomineeCount}
				isComplete={isComplete}
				nominationForms={nominationForms}
				nominationFormsLoading={nominationFormsLoading}
			/>
		</Modal>
	);
};

export default withApollo(ManageNominatedRewardContainer);
